import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getServices } from '../../../actions/services';

interface UsePublicationLogicProps {
  publication: any;
  brandsDealerships?: any[];
  selectedDealership?: any;
  setSelectedDealership?: any;
  selectedVersion?: string;
  setSelectedVersion?: any;
  setVehicleTypeReducer?: any;
  publications?: any;
  type?: string;
  articleData?: any;
}

export const usePublicationLogic = ({
  publication,
  brandsDealerships,
  selectedDealership,
  setSelectedDealership,
  selectedVersion,
  setSelectedVersion,
  setVehicleTypeReducer,
  publications,
  type = 'publication',
  articleData
}: UsePublicationLogicProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [openAdditionalModal, setOpenAdditionalModal] = useState(false);

  // Calcular precio mínimo
  const getMinPrice = useMemo(() => {
    let minPrice = publication?.price || 0;

    if (publication?.vehicles && publication.vehicles.length > 0) {
      publication.vehicles.forEach((vehicle: any) => {
        if (vehicle.price && (vehicle.price < minPrice || minPrice === 0)) {
          minPrice = vehicle.price;
        }
      });
    }

    return minPrice;
  }, [publication]);

  // Obtener datos del vehículo seleccionado para marcas
  const selectedVehicleData = useMemo(() => {
    if (!publication?.vehicles || !selectedVersion) {
      return {
        year: publication?.year,
        mileage: publication?.mileage
      };
    }

    const selectedVehicle = publication.vehicles.find((v: any) => v._id === selectedVersion);
    return {
      year: selectedVehicle?.year || publication?.year,
      mileage: selectedVehicle?.mileage || publication?.mileage
    };
  }, [publication, selectedVersion]);

  // Efecto para seleccionar la versión inicial
  useEffect(() => {
    if (publication?.vehicles && publication.vehicles.length > 0 && !selectedVersion) {
      const sortedVehicles = [...publication.vehicles].sort((a, b) => (a.price || 0) - (b.price || 0));
      setSelectedVersion?.(sortedVehicles[0]._id);
    }
  }, [publication, selectedVersion, setSelectedVersion]);

  // Efecto para seleccionar el concesionario inicial
  useEffect(() => {
    if (brandsDealerships && brandsDealerships.length > 0) {
      const dealerIds = brandsDealerships.map((dealer: any) => dealer.id || dealer._id);
      if (!selectedDealership || !dealerIds.includes(selectedDealership)) {
        const firstDealerId = brandsDealerships[0].id || brandsDealerships[0]._id;
        setSelectedDealership?.(firstDealerId);
      }
    }
  }, [brandsDealerships, selectedDealership, setSelectedDealership]);

  // Efecto para establecer el tipo de vehículo basado en la URL
  useEffect(() => {
    if (location.pathname.includes('nuevos') || location.pathname.includes('usados')) {
      const vehicleType = location.pathname.includes('nuevos') ? 'new' : 'used';
      setVehicleTypeReducer?.(vehicleType);
    }
  }, [location.pathname, setVehicleTypeReducer]);

  // Service handlers
  const handleServiceRequest = (formData: any, serviceType: string, businessType: string, brand?: any) => {
    if (!publication) {
      console.error('No publication data available');
      return;
    }
    
    const userData = {
      name: formData.nombre,
      last_name: formData.apellido,
      legal_id: formData.rut || '',
      email: formData.email,
      phone: formData.areaCode + formData.telefono
    };
    
    // Handle both publication structures (with or without .data)
    const pubData = publication.data || publication;
    
    const publicationId = pubData.id_publication || pubData._id || pubData.id || '';
    const vehicleId = articleData?._id || selectedVersion || pubData?.article_id || '';
    const vehicleImage = pubData?.pictures?.gallery?.[0]?.path || pubData?.pictures?.[0]?.path || '';
    const vehiclePrice = pubData?.price || 0;
    const typePublication = pubData?.type === 'new' ? 'new' : 'used';
    
    getServices(
      publicationId,
      userData,
      typePublication,
      businessType,
      vehicleId,
      vehicleImage,
      vehiclePrice,
      selectedDealership || '',
      serviceType,
      brand
    )(dispatch);
    
  };
  
  // MOK service (warranty)
  const handleMokService = (formData: any, brand: any) => {
    handleServiceRequest(formData, 'warranty', 'C2C', brand);
  };
  
  // MAVI service (GPS)
  const handleMaviService = (formData: any, brand: any) => {
    handleServiceRequest(formData, 'gps', 'B2C', brand);
  };
  
  // FULLMEC service (inspection)
  const handleFullMecService = (formData: any, brand: any) => {
    handleServiceRequest(formData, 'inspection', 'C2C', brand);
  };
  
  // ICAR TAG service
  const handleIcarTagService = (formData: any, brand: any) => {
    handleServiceRequest(formData, 'tag', 'C2C', brand);
  };
  
  // HDI service (insurance)
  const handleHDIService = (formData: any, brand: any) => {
    handleServiceRequest(formData, 'insurance', 'C2C', brand);
  };

  return {
    getMinPrice,
    selectedVehicleData,
    openAdditionalModal,
    setOpenAdditionalModal,
    handleMokService,
    handleMaviService,
    handleFullMecService,
    handleIcarTagService,
    handleHDIService
  };
};
